import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth as useClerkAuth, useClerk } from '@clerk/clerk-react';
import api, { setAuthToken } from '../utils/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const { isSignedIn, isLoaded: clerkLoaded, getToken } = useClerkAuth();
  const clerk = useClerk();

  const [user, setUser] = useState(null);
  const [studio, setStudio] = useState(null);
  const [loading, setLoading] = useState(true);

  // Set up auth token interceptor
  useEffect(() => {
    if (isSignedIn) {
      setAuthToken(getToken);
    }
  }, [isSignedIn, getToken]);

  // Fetch user data from our backend
  const fetchUser = useCallback(async () => {
    if (!isSignedIn) {
      setUser(null);
      setStudio(null);
      setLoading(false);
      return;
    }

    try {
      const token = await getToken();

      const signupRole = sessionStorage.getItem('dancehall_signup_role');
      const roleParam = signupRole ? `?role=${signupRole}` : '';
      if (signupRole) sessionStorage.removeItem('dancehall_signup_role');

      const { data } = await api.get(`/auth/me${roleParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(data.user);
      setStudio(data.studio);
    } catch (error) {
      console.error('Failed to fetch user:', error.message);
      setUser(null);
      setStudio(null);
    } finally {
      setLoading(false);
    }
  }, [isSignedIn, getToken]);

  // Set page title + favicon based on studio
  useEffect(() => {
    if (studio?.name) document.title = studio.name;
    if (studio?.branding?.logo) {
      const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
      link.rel = 'icon';
      link.href = studio.branding.logo;
      document.head.appendChild(link);
    }
  }, [studio]);

  useEffect(() => {
    if (clerkLoaded) fetchUser();
  }, [clerkLoaded, fetchUser]);

  const login = (options = {}) => {
    const clerkOptions = {
      appearance: {
        variables: { colorPrimary: '#3F51B5' },
        elements: {
          footerAction: { display: 'none' }
        }
      }
    };
    if (options.studioName) {
      clerkOptions.appearance.elements.headerTitle = { display: 'none' };
      clerkOptions.appearance.elements.headerSubtitle = { display: 'none' };
      clerkOptions.localization = {
        signIn: {
          start: {
            title: `Sign in to ${options.studioName}`,
            subtitle: 'Welcome back! Please sign in to continue'
          }
        },
        signUp: {
          start: {
            title: `Sign up for ${options.studioName}`,
            subtitle: 'Create your account to get started'
          }
        }
      };
    }
    if (options.mode === 'signup') {
      clerk.openSignUp(clerkOptions);
    } else {
      clerk.openSignIn(clerkOptions);
    }
  };

  const logout = () => {
    clerk.signOut();
    setUser(null);
    setStudio(null);
  };

  return (
    <AuthContext.Provider value={{
      user,
      studio,
      isAuthenticated: !!isSignedIn,
      loading: !clerkLoaded || loading,
      login,
      logout,
      refreshUser: fetchUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
